import { useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { usePlatformIdleTimeout } from "@/hooks/use-platform-idle-timeout";
import { refreshPlatformSessionOnce } from "@/services/http/platform-session-refresh";
import { usePlatformAuthStore } from "@/stores/platformAuthStore";

/**
 * MT-1 AUTH-009 S12: only a platform session opens a console page. After a reload the access
 * token (memory only) is restored from the `lms_platform_refresh` cookie; without a platform
 * session the page goes to `/platform/login`. A school session never opens it.
 */
export function PlatformGate({ children }: { children: (email: string) => ReactNode }) {
  const user = usePlatformAuthStore((state) => state.user);
  const isAuthenticated = usePlatformAuthStore((state) => state.isAuthenticated);
  const [restoring, setRestoring] = useState(!isAuthenticated);
  usePlatformIdleTimeout();

  useEffect(() => {
    if (isAuthenticated) return;
    let live = true;
    void refreshPlatformSessionOnce().finally(() => {
      if (live) setRestoring(false);
    });
    return () => {
      live = false;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !user) {
    if (restoring) return null;
    return <Navigate to="/platform/login" replace />;
  }
  return <>{children(user.email)}</>;
}
